import React, { useState, useEffect, useRef, useCallback } from 'react';
import Header from '../components/header';
import { useLocation, Link } from 'react-router-dom';
import '../css/sidebar.css';
import { AiOutlineCloudUpload, AiOutlineSearch } from 'react-icons/ai';
import logo from '../img/logo-softmining.svg';
import { FaArrowRight } from 'react-icons/fa';
import { Stage } from 'ngl';
import PdbStorageLoader from '../components/PDBStorageLoader';
import { retrieveMineData } from "../Utility/ConsumeToken/consumeToken";
import { getFilePDB } from "../Utility/ManageFiles/fileUtils";
import { useAuth } from "../context/AuthContext";


export default function ViewerPDB() {
    const location = useLocation();
    const [file, setFile] = useState(null);
    const [loadfile, setLoadFile] = useState(null);
    const [repr, setRepr] = useState('cartoon');
    const [showLigands, setShowLigands] = useState(false);
    const [selection, setSelection] = useState('');
    const [mode, setMode] = useState('single');
    const [pdbId, setPdbId] = useState('');
    const { user } = useAuth();             
    const isAdmin = user?.data?.admin === true;

    // stato per la sidebar mobile a scomparsa
    const [isSidebarOpen, setIsSidebarOpen] = useState(true);

    const stageRef = useRef(null);
    const compRef = useRef(null);
    const ligRef = useRef(null);
    const containerRef = useRef(null);

    // Cleanup function
    const cleanupStage = () => {
        const stage = stageRef.current;
        if (stage) {
            if (stage.resizeHandler) {
                window.removeEventListener('resize', stage.resizeHandler, false);
            }
            stage.removeAllComponents();
            stage.dispose();
            stageRef.current = null;
            compRef.current = null;
            ligRef.current = null;
        }
        if (containerRef.current) {
            containerRef.current.innerHTML = '';
        }
    };

    const loadPdbFromFile = useCallback(
        async inputFile => {
            cleanupStage();

            let pdbText;

            // 1) Se è un vero File/Blob
            if (inputFile instanceof Blob) {
                pdbText = await inputFile.text();
            }
            // 2) Se è già il testo
            else if (typeof inputFile === 'string') {
                pdbText = inputFile;
            }
            // 3) Se è l’oggetto pipeline con { url: "blob:..." }
            else if (inputFile && inputFile.url) {
                const resp = await fetch(inputFile.url);
                pdbText = await resp.text();
            } else {
                console.error('loadPdbFromFile: formato non gestito', inputFile);
                return;
            }

            console.log('pdbText length:', pdbText.length);

            const stage = new Stage(containerRef.current, { backgroundColor: 'white' });
            stageRef.current = stage;
            const resizeHandler = () => stage.handleResize();
            stage.resizeHandler = resizeHandler;
            window.addEventListener('resize', resizeHandler, false);

            const blob = new Blob([pdbText], { type: 'text/plain' });
            blob.name = inputFile.name || 'pipeline.pdb';

            stage
                .loadFile(blob, { ext: 'pdb' })
                .then(comp => {
                    compRef.current = comp;
                    comp.addRepresentation('cartoon', { color: 'chainname' });
                    comp.autoView();
                })
                .catch(err => console.error('NGL load error:', err));
        },
        [containerRef]
    );

    const handleFileChange = e => setFile(e.target.files[0] || null);

    const applySelection = () => {
        const comp = compRef.current;
        if (!comp || !selection) return;
        comp.addRepresentation('spacefill', { sele: selection, color: 'element' });
        comp.autoView(selection);
    };

    const toggleLigands = () => {
        setShowLigands(prev => !prev);
    };

    // Aggiorna le rappresentazioni quando cambiano repr o showLigands
    useEffect(() => {
        const comp = compRef.current;
        if (!comp) return;

        comp.removeAllRepresentations();
        ligRef.current = null;

        comp.addRepresentation(repr, { color: 'chainname' });

        if (showLigands) {
            ligRef.current = comp.addRepresentation('ball+stick', { sele: 'hetero' });
        }

        comp.autoView();
    }, [repr, showLigands]);

    const handleSubmitId = async e => {
        e.preventDefault();
        setLoadFile(true);

        await new Promise(resolve => requestAnimationFrame(resolve));

        cleanupStage();
        const stage = new Stage(containerRef.current, { backgroundColor: 'white' });
        stageRef.current = stage;
        const resizeHandler = () => stage.handleResize();
        stage.resizeHandler = resizeHandler;
        window.addEventListener('resize', resizeHandler, false);

        const id = pdbId.trim().toUpperCase();
        stage
            .loadFile(`rcsb://${id}`, { defaultRepresentation: false })
            .then(comp => {
                compRef.current = comp;
                comp.addRepresentation('cartoon', { color: 'chainname' });
                comp.autoView();
            })
            .catch(err => console.error('NGL load error:', err));
    };

    const handleSubmitFile = e => {
        e.preventDefault();
        setLoadFile(true);
        loadPdbFromFile(file);
    };

    // Cleanup stage on unmount
    useEffect(() => () => cleanupStage(), []);

    // Se la pagina è parte di una pipeline, carica il file PDB passato da pipeline
    useEffect(() => {
        if (location.state?.params?.pdbResults?.length) {
            const pipedFile = location.state.params.pdbResults[0];
            setFile(pipedFile);
            setLoadFile(true);
            loadPdbFromFile(pipedFile);
        }
    }, [location.state, loadPdbFromFile]);

    // 🔹 NUOVO: gestione comportamento sidebar desktop/mobile
    useEffect(() => {
        const handleResize = () => {
            if (window.innerWidth > 1100) {
                setIsSidebarOpen(true); // desktop: sempre aperta
            } else {
                setIsSidebarOpen(false); // mobile: parte chiusa
            }
        };

        handleResize();
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    const handleVisualize = useCallback(
        async (fileName) => {
            try {
            // 1) Passiamo alla modalità viewer (così viene montato il containerRef)
            setLoadFile(true);

            // 2) Aspettiamo il prossimo frame perché il DOM si aggiorni
            await new Promise((resolve) => requestAnimationFrame(resolve));

            // 3) Recuperiamo il file dallo storage
            const apitoken = await retrieveMineData();
            const fileData = await getFilePDB("CLEAN_PDB", fileName, apitoken);

            // 4) Creiamo il Blob PDB
            const blob = new Blob([fileData.output], { type: "chemical/x-pdb" });
            blob.name = fileName; // proprietà custom che poi usi in loadPdbFromFile

            // 5) Salviamo in state (se ti serve altrove) e carichiamo nello Stage
            setFile(blob);
            await loadPdbFromFile(blob);
            } catch (error) {
            console.error("Errore nella visualizzazione del file", error);
            }
        },
        [loadPdbFromFile]
        );


    // Se non c'è contenuto PDB, mostriamo il form di caricamento
    if (!loadfile) {
        return (
            <>
                <Header />
                <div className="riga headerTools">
                    <h3 className="titolo titoloFeature">ViewerPDB</h3>
                    <p className="text description">
                        ViewerPDB is a user-friendly visualization tool for exploring protein structures
                        and molecular complexes. It allows researchers to interactively inspect atomic
                        details, analyze conformational changes, and gain structural insights with
                        high-quality 3D rendering. Whether for research, education, or presentation
                        purposes, ViewerPDB provides an intuitive and efficient molecular visualization
                        experience.
                    </p>
                    <div className="space"></div>
                </div>
                <center>
                    <div className="form formLogin formTools">
                        <img src={logo} className="sign-logo" alt="logo" />

                        {/* Mode Selection - SOLO ADMIN */}
                        {isAdmin && (
                        <>
                            <label className="selectLabel">
                            Choose if to send a .pdb file or a PDB ID:
                            </label>
                            <div className="mode-selection">
                            <button
                                type="button"
                                className={`mode-button ${mode === 'single' ? 'active' : ''}`}
                                onClick={() => setMode('single')}
                            >
                                PDB ID
                            </button>
                            <button
                                type="button"
                                className={`mode-button ${mode === 'file' ? 'active' : ''}`}
                                onClick={() => setMode('file')}
                            >
                                .PDB file
                            </button>
                            </div>
                        </>
                        )}

                        {/* Se ADMIN: usa lo switch mode; se NON admin: mostra sempre il blocco "single" */}
                        {(isAdmin ? mode === 'single' : true) && (
                        <>
                            <form onSubmit={handleSubmitId}>
                            <div>
                                <label htmlFor="pdbId">PDB ID:</label>
                                <input
                                placeholder="Enter PDB ID..."
                                type="text"
                                id="pdbId"
                                value={pdbId}
                                onChange={e => setPdbId(e.target.value)}
                                required
                                style={{ textTransform: 'uppercase' }}
                                />
                            </div>
                            <button type="submit" className="button sign-button">
                                Submit
                            </button>
                            <p className="textOrForm text">- or -</p>
                            <PdbStorageLoader
                                buttonLabel="Choose from CleanPDB Storage"
                                buttonClassName="sign-button button download-button"
                                textAction="View PDB"
                                onLoad={(fileName) => {
                                // opzionale: popolare anche l'input con l’ID
                                const pdbIdFromFile =
                                    fileName.toUpperCase().endsWith(".PDB")
                                    ? fileName.slice(0, -4)
                                    : fileName;

                                setPdbId(pdbIdFromFile);
                                handleVisualize(fileName);
                                }}
                            />
                            </form>
                        </>
                        )}

                        {isAdmin && mode === 'file' && (
                        <>
                            <form onSubmit={handleSubmitFile}>
                            <label className="selectLabel">
                                Select a PDB file for 3D exploration:
                            </label>
                            <div className="file-upload-container">
                                <label
                                htmlFor="fileInput"
                                className="custom-file-label textlink"
                                >
                                <AiOutlineCloudUpload className="upload-icon textlink" />
                                {!file && (
                                    <span className="noBottomSpace">
                                    Upload a PDB file
                                    </span>
                                )}
                                {file && (
                                    <span className="noBottomSpace">{file.name}</span>
                                )}
                                </label>
                                <input
                                type="file"
                                accept=".pdb"
                                id="fileInput"
                                onChange={handleFileChange}
                                style={{ display: 'none' }}
                                required
                                />
                            </div>
                            <button type="submit" className="button sign-button">
                                Submit
                            </button>
                            </form>
                        </>
                        )}

                        <div className="riga">
                            <Link
                                className="textlink textLinkSign"
                                to="/dashboard"
                            >
                                Back to Dashboard{' '}
                                <FaArrowRight
                                    size={12}
                                    style={{ marginLeft: '8px', marginBottom: '-1px' }}
                                />
                            </Link>
                        </div>
                    </div>
                    <div className="space"></div>
                </center>
            </>
        );
    }

    // Se è stato caricato un file PDB (o ricevuto da pipeline), mostriamo il viewer
    return (
        <>
            <Header />
            <div className="riga headerTools headerSidebarTools">
                <h3 className="titolo titoloFeature">ViewerPDB</h3>
                <p className="text description descriptionSidebarTools">
                    ViewerPDB is a user-friendly visualization tool for exploring protein structures
                    and molecular complexes. It allows researchers to interactively inspect atomic
                    details, analyze conformational changes, and gain structural insights with
                    high-quality 3D rendering. Whether for research, education, or presentation
                    purposes, ViewerPDB provides an intuitive and efficient molecular visualization
                    experience.
                </p>
                <div className="space"></div>

                {/* NUOVO: bottone mobile per aprire/chiudere le impostazioni */}
                <button
                    type="button"
                    className="button sign-button sidebar-toggle"
                    onClick={() => setIsSidebarOpen(prev => !prev)}
                >
                    {isSidebarOpen ? 'Hide settings' : 'Show settings'}
                </button>
            </div>

            <div className="bodysidebar-layout">
                <div className={`ManageAreaSidebar ${isSidebarOpen ? 'is-open' : ''}`}>
                    {/* VIEW SETTINGS */}
                    <h2 className="titolo">
                        <center>VIEW SETTINGS</center>
                    </h2>

                    <div style={{ width: '100%' }}>
                        <label> Representation: </label>
                        <select
                            value={repr}
                            onChange={e => setRepr(e.target.value)}
                        >
                            <option value="cartoon">Cartoon</option>
                            <option value="surface">Surface</option>
                            <option value="ball+stick">Ball+Stick</option>
                            <option value="licorice">Licorice</option>
                        </select>
                    </div>

                    <div style={{ width: '100%' }}>
                        <label>
                            {' '}
                            Click button to {showLigands ? 'hide' : 'show'} ligands:{' '}
                        </label>
                        <button
                            onClick={toggleLigands}
                            className="button sign-button export-button"
                        >
                            {showLigands ? 'Hide Ligands' : 'Show Ligands'}
                        </button>
                    </div>

                    <div style={{ width: '100%' }}>
                        <label> Search a specific atom: </label>
                        <form
                            onSubmit={e => {
                                e.preventDefault();
                                applySelection();
                            }}
                        >
                            <div className="settings-search-pdbviewer">
                                <input
                                    type="text"
                                    placeholder="e.g. :A and .CA"
                                    value={selection}
                                    onChange={e => setSelection(e.target.value)}
                                    onKeyDown={e => {
                                        if (e.key === 'Enter') {
                                            e.preventDefault();
                                            applySelection();
                                        }
                                    }}
                                />
                                <button type="submit" disabled={!selection}>
                                    <AiOutlineSearch size={25} />
                                </button>
                            </div>
                        </form>
                    </div>
                        <div className="riga">
                            <Link
                                className="textlink textLinkSign"
                                to="/dashboard"
                            >
                                Back to Dashboard{' '}
                                <FaArrowRight
                                    size={12}
                                    style={{ marginLeft: '8px', marginBottom: '-1px' }}
                                />
                            </Link>
                        </div>
                </div>

                <div ref={containerRef} className="graph-body-sidebar" />
                <div className='spaceGraph'></div>
            </div>
        </>
    );
}
